import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, ArrowLeft } from 'lucide-react';
import { API_BASE_URL } from '../../config/environment';

export default function RSVPManagement() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [rsvps, setRSVPs] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (eventId) loadRSVPs();
  }, [eventId]);

  const loadRSVPs = async () => {
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/events/${eventId}/rsvps`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const data = await response.json();
      setRSVPs(data.rsvps);
      setStats({
        total: data.total,
        confirmed: data.confirmed,
        checked_in: data.checked_in,
        cancelled: data.cancelled
      });
      
      if (data.rsvps.length > 0) {
        const eventRes = await fetch(`${API_BASE_URL}/api/events/${eventId}`);
        const eventData = await eventRes.json();
        setEvent(eventData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Company', 'Has Guest', 'Guest Name', 'Status', 'Checked In', 'Created At'];
    const rows = rsvps.map(r => [
      r.first_name, r.last_name, r.email, r.mobile_phone || '', r.company || '',
      r.has_guest ? 'Yes' : 'No',
      r.has_guest ? `${r.guest_first_name || ''} ${r.guest_last_name || ''}` : '',
      r.status, r.checked_in ? 'Yes' : 'No',
      new Date(r.created_at).toLocaleString()
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rsvps-${event?.slug || eventId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <div className="bg-white border-b border-[#E1DFD7]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/events')} className="flex items-center gap-2 text-[#4C5A70] hover:text-[#0B2240]">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <div>
                <h1 className="font-heading text-2xl font-semibold text-[#0B2240]">{event?.title || 'RSVPs'}</h1>
                {event && <p className="text-sm text-[#4C5A70]">{new Date(event.start_datetime).toLocaleDateString()} • {event.location_city}</p>}
              </div>
            </div>
            <button onClick={exportCSV} disabled={rsvps.length === 0} className="flex items-center gap-2 px-4 py-2 bg-[#1FA3A3] text-white rounded-lg hover:bg-[#157A7A] disabled:opacity-50">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        {/* Capacity Breakdown */}
        {event && event.capacity && (
          <div className="bg-white rounded-lg p-6 mb-6 border border-[#E1DFD7]">
            <h3 className="font-heading text-lg font-semibold text-[#0B2240] mb-4">📊 Capacity Overview</h3>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4 text-center">
              <div>
                <div className="text-2xl font-semibold text-[#0B2240]">{event.capacity}</div>
                <div className="text-sm text-[#4C5A70]">Total Capacity</div>
              </div>
              {event.blocked_seats > 0 && (
                <div>
                  <div className="text-2xl font-semibold text-[#D4AF37]">{event.blocked_seats}</div>
                  <div className="text-sm text-[#4C5A70]">🔒 Blocked</div>
                </div>
              )}
              <div>
                <div className="text-2xl font-semibold text-[#1FA3A3]">
                  {event.capacity - (event.blocked_seats || 0)}
                </div>
                <div className="text-sm text-[#4C5A70]">Public Capacity</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-[#0B2240]">{stats?.confirmed || 0}</div>
                <div className="text-sm text-[#4C5A70]">RSVPs</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-[#1FA3A3]">
                  {Math.max(0, event.capacity - (event.blocked_seats || 0) - (stats?.confirmed || 0))}
                </div>
                <div className="text-sm text-[#4C5A70]">Available</div>
              </div>
            </div>
          </div>
        )}
        
        {/* RSVP Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl font-semibold text-[#0B2240]">{stats.total}</div>
              <div className="text-sm text-[#4C5A70]">Total</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl font-semibold text-[#1FA3A3]">{stats.confirmed}</div>
              <div className="text-sm text-[#4C5A70]">Confirmed</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl font-semibold text-[#D4AF37]">{stats.checked_in}</div>
              <div className="text-sm text-[#4C5A70]">Checked In</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl font-semibold text-[#4C5A70]">{stats.cancelled}</div>
              <div className="text-sm text-[#4C5A70]">Cancelled</div>
            </div>
          </div>
        )}

        {rsvps.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <p className="text-[#4C5A70]">No RSVPs yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-soft border border-[#E1DFD7] overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F7F6F2] border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase">Guest</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#4C5A70] uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1DFD7]">
                {rsvps.map(r => (
                  <tr key={r.id} className="hover:bg-[#F7F6F2]">
                    <td className="px-6 py-4 font-medium text-[#0B2240]">{r.first_name} {r.last_name}</td>
                    <td className="px-6 py-4 text-sm text-[#4C5A70]">{r.email}</td>
                    <td className="px-6 py-4 text-sm text-[#4C5A70]">{r.company || '-'}</td>
                    <td className="px-6 py-4 text-sm text-[#4C5A70]">
                      {r.has_guest ? `${r.guest_first_name} ${r.guest_last_name}` : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        r.status === 'confirmed' ? 'bg-[#1FA3A3]/10 text-[#1FA3A3]' :
                        r.status === 'checked_in' ? 'bg-[#0B2240] text-white' :
                        'bg-[#EDEBE6] text-[#4C5A70]'
                      }`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
